import React from 'react';
import {
  PlusIcon,
  FolderOpenIcon,
  BookmarkIcon,
  ArrowUpTrayIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  InformationCircleIcon,
  UserPlusIcon,
  MapPinIcon,
  ClockIcon,
  CalculatorIcon,
  TableCellsIcon,
} from '@heroicons/react/24/outline';

export type ButtonConfig = {
  key: string;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  onClick: () => void;
  variant?: 'primary' | 'secondary';
  disabled?: boolean;
  loading?: boolean;
  loadingText?: string;
  tooltip?: string;
};

export interface PageToolbarProps {
  // Page identification
  title: string;
  icon?: React.ComponentType<{ className?: string }>;

  // Buttons configuration
  buttons: ButtonConfig[];

  // Status information (optional). Rendered as a banner row beneath the
  // toolbar so long messages never push the toolbar's buttons around.
  statusInfo?: {
    sourceWorkspace?: string;
    validationMessage?: string;
    isWarning?: boolean;
    workspaceName?: string;
    hasUnsavedChanges?: boolean;
    // Informational status messages (e.g. "Analysis is out of date").
    // Each entry renders as an inline amber row in the banner,
    // wrapped in an aria-live="polite" region so screen readers announce changes.
    infoMessages?: string[];
  };

  className?: string;
}

// Icon map for common actions
export const ToolbarIcons = {
  NewWorkspace: PlusIcon,
  NewForecast: ArrowTrendingUpIcon,
  LoadWorkspace: FolderOpenIcon,
  LoadForecast: FolderOpenIcon,
  SaveWorkspace: BookmarkIcon,
  SaveForecast: BookmarkIcon,
  Export: ArrowUpTrayIcon,
  Calculate: CalculatorIcon,
  AddUser: UserPlusIcon,
  AddSite: MapPinIcon,
  History: ClockIcon,
  Analysis: ChartBarIcon,
  Table: TableCellsIcon,
};

const PageToolbar: React.FC<PageToolbarProps> = ({
  title,
  icon: Icon = ChartBarIcon,
  buttons,
  statusInfo,
  className = '',
}) => {
  // Separate primary and secondary buttons
  const primaryButtons = buttons.filter(b => b.variant === 'primary');
  const secondaryButtons = buttons.filter(b => b.variant !== 'primary');

  const hasStatusContent = !!statusInfo && (
    !!statusInfo.sourceWorkspace ||
    !!statusInfo.validationMessage ||
    (statusInfo.infoMessages?.length ?? 0) > 0 ||
    !!statusInfo.workspaceName ||
    !!statusInfo.hasUnsavedChanges
  );

  return (
    <div className={`bg-white border-b border-gray-200 ${className}`}>
      {/* Top row: title + buttons only. Status content is hoisted to the
          banner below so it can never reflow this row. */}
      <div className="px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Left Section: Title and Secondary Actions */}
          <div className="flex items-center space-x-4 min-h-[40px]">
            <div className="flex items-center space-x-2">
              <Icon className="h-5 w-5 text-blue-600" />
              <h1 className="text-xl font-semibold text-gray-900">{title}</h1>
            </div>

            {/* Secondary action buttons on the left */}
            {secondaryButtons.length > 0 && (
              <>
                <div className="h-6 w-px bg-gray-300" />
                {secondaryButtons.map((button, index) => (
                  <React.Fragment key={button.key}>
                    {index > 0 && index % 2 === 0 && <div className="h-6 w-px bg-gray-300" />}
                    <button
                      onClick={button.onClick}
                      disabled={button.disabled || button.loading}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      title={button.tooltip}
                    >
                      {button.icon && <button.icon className="h-4 w-4 mr-2" />}
                      {button.loading ? button.loadingText || 'Loading...' : button.label}
                    </button>
                  </React.Fragment>
                ))}
              </>
            )}
          </div>

          {/* Right Section: Primary Actions only */}
          <div className="flex items-center space-x-4 min-h-[40px]">
            {primaryButtons.map((button, index) => (
              <React.Fragment key={button.key}>
                {index > 0 && <div className="h-6 w-px bg-gray-300" />}
                <button
                  onClick={button.onClick}
                  disabled={button.disabled || button.loading}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title={button.tooltip}
                >
                  {button.icon && <button.icon className="h-4 w-4 mr-2" />}
                  {button.loading ? button.loadingText || 'Loading...' : button.label}
                </button>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Status banner — only rendered when there is content to show, so pages
          without status look identical to before. flex-wrap lets long info
          messages occupy the full row without pushing the toolbar buttons. */}
      {hasStatusContent && statusInfo && (
        <div className="px-6 py-2 border-t border-gray-100 bg-gray-50/50">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
            {/* Source Workspace (for forecasting) */}
            {statusInfo.sourceWorkspace && (
              <div className="flex items-center space-x-2 text-gray-600">
                <ArrowTrendingUpIcon className="h-4 w-4 flex-shrink-0" />
                <span>Source: {statusInfo.sourceWorkspace}</span>
              </div>
            )}

            {/* Validation Message */}
            {statusInfo.validationMessage && (
              <div className={`flex items-center space-x-2 ${statusInfo.isWarning ? 'text-amber-600' : 'text-gray-600'}`}>
                <InformationCircleIcon className="h-4 w-4 flex-shrink-0" />
                <span>{statusInfo.validationMessage}</span>
              </div>
            )}

            {/* Workspace Status */}
            {statusInfo.workspaceName && !statusInfo.sourceWorkspace && (
              <div className="flex items-center text-gray-600">
                <span className="font-medium">Workspace:</span>
                <span className="ml-1 text-blue-600">{statusInfo.workspaceName}</span>
                {statusInfo.hasUnsavedChanges && <span className="ml-1 text-amber-600">*</span>}
              </div>
            )}

            {/* Unsaved Changes Indicator */}
            {statusInfo.hasUnsavedChanges && (
              <div className="flex items-center px-2 py-1 bg-amber-100 text-amber-800 text-xs rounded-full">
                <div className="w-1.5 h-1.5 bg-amber-500 rounded-full mr-1"></div>
                <span>Unsaved changes</span>
              </div>
            )}

            {/* Info Messages — each occupies its own flex item; the wrapping
                parent lets long messages take the full row width and wrap to
                multiple lines without disturbing the toolbar above. */}
            {statusInfo.infoMessages && statusInfo.infoMessages.length > 0 && (
              <div role="status" aria-live="polite" className="flex flex-wrap items-center gap-x-4 gap-y-2 w-full">
                {statusInfo.infoMessages.map((msg) => (
                  <div key={msg} className="flex items-start space-x-2 text-amber-600">
                    <InformationCircleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    <span>{msg}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PageToolbar;
